/**
 * Marketing analytics + attribution (GTM funnel — Phase 0 task 6).
 *
 * One small, dependency-free layer that:
 *   - loads Google Analytics 4 (gtag) + the Meta Pixel (fbq), each gated on an
 *     env var so it's a complete no-op until an ID is configured;
 *   - captures first-touch UTM / click-id attribution into localStorage;
 *   - exposes a single typed `track()` that fans an event out to BOTH platforms;
 *   - only ever runs on the DEMO / public-funnel surfaces — never on a real
 *     tenant's customer or admin pages (see `shouldLoadMarketingAnalytics`).
 *
 * IDs come from Vite env (build-time): VITE_GA_MEASUREMENT_ID (G-XXXXXXX),
 * VITE_META_PIXEL_ID (numeric). VITE_SCHEDULER_URL is the "book a walkthrough"
 * destination. All optional — unset = that piece is skipped, nothing throws.
 *
 * NOTE (CSP, ref CLAUDE.md audit item L1): CSP is not enabled yet. When it is,
 * GA (googletagmanager.com / google-analytics.com) and Meta
 * (connect.facebook.net) must be allowlisted, or these injected scripts break.
 */

const GA_ID = (import.meta.env.VITE_GA_MEASUREMENT_ID as string | undefined)?.trim() || "";
const META_PIXEL_ID = (import.meta.env.VITE_META_PIXEL_ID as string | undefined)?.trim() || "";
const SCHEDULER_URL = (import.meta.env.VITE_SCHEDULER_URL as string | undefined)?.trim() || "";

const ATTRIBUTION_KEY = "fo_attribution_v1"; // first-touch, persisted

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    fbq?: ((...args: unknown[]) => void) & { callMethod?: (...args: unknown[]) => void; queue?: unknown[] };
    _fbq?: unknown;
  }
}

export interface Attribution {
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmTerm?: string;
  utmContent?: string;
  referrer?: string;
  landingPath?: string;
}

/**
 * Marketing pixels fire ONLY on the demo + public funnel — never on a real
 * tenant's app. The demo lives at `/demo*` and, once started, under the
 * `demo-public` org, so those are the only surfaces we instrument. This keeps a
 * tenant's own customers out of FreshOrder's retargeting/analytics.
 */
export function shouldLoadMarketingAnalytics(pathname: string = window.location.pathname): boolean {
  if (pathname.startsWith("/demo")) return true;
  if (pathname.startsWith("/org/demo-public")) return true;
  return false;
}

let initialized = false;

/** Idempotent. Loads GA4 + Meta Pixel if their IDs are set and we're on a funnel surface. */
export function initAnalytics(): void {
  if (initialized) return;
  if (typeof window === "undefined") return;
  if (!shouldLoadMarketingAnalytics()) return;
  initialized = true;

  captureAttribution();

  if (GA_ID) loadGoogleAnalytics(GA_ID);
  if (META_PIXEL_ID) loadMetaPixel(META_PIXEL_ID);
}

function loadGoogleAnalytics(id: string): void {
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
  document.head.appendChild(s);

  window.dataLayer = window.dataLayer || [];
  const gtag = (...args: unknown[]) => { window.dataLayer!.push(args); };
  window.gtag = gtag;
  gtag("js", new Date());
  // SPA: we send page_view manually on route changes, not automatically.
  gtag("config", id, { send_page_view: false });
}

function loadMetaPixel(id: string): void {
  /* Standard Meta Pixel base code, TS-safe. */
  if (window.fbq) return;
  const n: NonNullable<Window["fbq"]> = function (...args: unknown[]) {
    if (n.callMethod) n.callMethod(...args);
    else (n.queue = n.queue || []).push(args);
  } as NonNullable<Window["fbq"]>;
  window.fbq = n;
  if (!window._fbq) window._fbq = n;
  n.queue = [];
  const s = document.createElement("script");
  s.async = true;
  s.src = "https://connect.facebook.net/en_US/fbevents.js";
  document.head.appendChild(s);

  window.fbq!("init", id);
  window.fbq!("track", "PageView");
}

const UTM_PARAMS: Array<[keyof Attribution, string]> = [
  ["utmSource", "utm_source"],
  ["utmMedium", "utm_medium"],
  ["utmCampaign", "utm_campaign"],
  ["utmTerm", "utm_term"],
  ["utmContent", "utm_content"],
];

/**
 * Capture FIRST-TOUCH attribution. If a stored record already exists it wins
 * (first touch is the durable one); we only fill it on the visitor's first hit
 * that carries any UTM/referrer signal.
 */
export function captureAttribution(): Attribution {
  const existing = getAttribution();
  if (existing && Object.keys(existing).length > 0) return existing;

  const params = new URLSearchParams(window.location.search);
  const attribution: Attribution = {};
  for (const [key, param] of UTM_PARAMS) {
    const v = params.get(param);
    if (v) attribution[key] = v.slice(0, 160);
  }
  attribution.landingPath = window.location.pathname + window.location.search;
  const ref = document.referrer;
  if (ref && !ref.includes(window.location.host)) attribution.referrer = ref.slice(0, 500);

  try {
    localStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(attribution));
  } catch { /* storage disabled — attribution is best-effort */ }
  return attribution;
}

export function getAttribution(): Attribution {
  try {
    const raw = localStorage.getItem(ATTRIBUTION_KEY);
    return raw ? (JSON.parse(raw) as Attribution) : {};
  } catch {
    return {};
  }
}

/** Fan a funnel event out to GA4 (custom event) and Meta (custom event). Safe no-op if unloaded. */
export function track(event: string, params: Record<string, unknown> = {}): void {
  try {
    window.gtag?.("event", event, params);
    window.fbq?.("trackCustom", event, params);
  } catch { /* never let analytics throw into product code */ }
}

/** SPA page_view for GA4 (Meta's PageView fires once at init). */
export function trackPageview(path: string = window.location.pathname): void {
  try {
    window.gtag?.("event", "page_view", { page_path: path });
  } catch { /* no-op */ }
}

/**
 * A lead was captured. Fires BOTH our custom funnel event and the platforms'
 * STANDARD lead events (GA4 `generate_lead`, Meta `Lead`) — the standard ones
 * are what ad-side conversion optimization keys on.
 */
export function trackLead(params: Record<string, unknown> = {}): void {
  try {
    window.gtag?.("event", "generate_lead", params);
    window.fbq?.("track", "Lead", params);
  } catch { /* no-op */ }
  track("demo_lead_submitted", params);
}

/** The "book a walkthrough" scheduler URL, or null if not configured. */
export function getSchedulerUrl(): string | null {
  return SCHEDULER_URL || null;
}

/** Append query params to a URL without clobbering existing ones. */
export function appendParams(url: string, params: Record<string, string | undefined>): string {
  try {
    const u = new URL(url, window.location.origin);
    for (const [k, v] of Object.entries(params)) {
      if (v && !u.searchParams.has(k)) u.searchParams.set(k, v);
    }
    return u.toString();
  } catch {
    return url;
  }
}
